import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { contactInfo } from "../data/products";
import { Send, Mail, Phone, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "../components/SEO";

export default function DevisPage() {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    adresse: "",
    echeance: "",
    pose: "",
    products: [],
    message: "",
    acceptTerms: false,
  });

  // Pre-fill message from calculator params
  useEffect(() => {
    const surface = searchParams.get("surface");
    const lames = searchParams.get("lames");
    const productName = searchParams.get("product");
    const lambourdes = searchParams.get("lambourdes");
    const clips = searchParams.get("clips");
    const demiclips = searchParams.get("demiclips");

    if (surface && lames) {
      const calcMessage = `--- Estimation calculateur ---\nProduit : ${productName || ""}\nSurface : ${surface} m²\nLames : ${lames} unités\nLambourdes : ${lambourdes || ""} unités\nClips Inox A4 : ${clips || ""} unités\nDemi-clips : ${demiclips || ""} unités\n---\n`;
      setFormData((prev) => ({
        ...prev,
        message: calcMessage,
        products: ["terrasse"],
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && name === "products") {
      const newProducts = checked
        ? [...formData.products, value]
        : formData.products.filter((p) => p !== value);
      setFormData((prev) => ({ ...prev, products: newProducts }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.acceptTerms) {
      toast.error("Veuillez accepter les conditions");
      return;
    }
    setIsSubmitting(true);

    // Format devis message
    const productsText = formData.products.length > 0
      ? formData.products.map(p => {
          const opt = productOptions.find(o => o.value === p);
          return opt ? opt.label : p;
        }).join(", ")
      : "Non précisé";

    const lines = [
      `*DEMANDE DE DEVIS — PROBWA*`,
      ``,
      `*Client :* ${formData.name}`,
      `*Email :* ${formData.email}`,
      `*Tél :* ${formData.phone}`,
      formData.adresse ? `*Adresse :* ${formData.adresse}` : null,
      formData.echeance ? `*Échéance :* ${formData.echeance}` : null,
      formData.pose ? `*Pose :* ${formData.pose}` : null,
      `*Produits :* ${productsText}`,
      ``,
      formData.message ? `*Message :*\n${formData.message}` : null,
    ].filter(Boolean).join("\n");

    // Send email via backend SMTP (OVH)
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiUrl}/api/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `Devis — ${formData.name}`,
          source: "Demande de Devis",
          fields: {
            Nom: formData.name,
            Email: formData.email,
            Téléphone: formData.phone,
            Adresse: formData.adresse || "Non précisé",
            Échéance: formData.echeance || "Non précisé",
            Pose: formData.pose || "Non précisé",
            Produits: productsText,
            Message: formData.message || "—",
          },
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Erreur serveur");
      }
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success("Demande envoyée !", {
        description: "Nous vous répondrons dans les plus brefs délais.",
      });
    } catch (err) {
      setIsSubmitting(false);
      toast.error("Erreur lors de l'envoi", {
        description: "Veuillez réessayer ou nous contacter par téléphone.",
      });
    }
  };

  const productOptions = [
    { value: "terrasse", label: "Lame de Terrasse" },
    { value: "bardage", label: "Bardage" },
    { value: "parquet", label: "Parquet" },
    { value: "tasseaux", label: "Tasseaux" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-[#F5F5F0]"
    >
      <Navbar />
      <SEO
        path="/demande-de-devis"
        title="Demande de Devis Gratuit — Terrasse Bambou La Réunion"
        description="Demandez votre devis gratuit pour terrasse, bardage ou parquet en bambou à La Réunion. Réponse sous 48h. Pose professionnelle, matériaux REBO Bamboo certifiés."
      />
      <main className="pt-24">
        {/* Hero */}
        <section className="py-12 md:py-16 bg-[#2D2D2D]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-[#E8E8E8] mb-4"
              style={{ fontFamily: "'DM Serif Display', serif" }}
              data-testid="devis-page-title"
            >
              DEMANDEZ VOTRE <span className="text-[#8BC34A]">DEVIS</span>
            </motion.h1>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-3 gap-12">
              {/* Form */}
              <div className="md:col-span-2">
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-3xl p-12 text-center shadow-xl"
                  >
                    <CheckCircle className="w-20 h-20 text-[#8BC34A] mx-auto mb-6" />
                    <h2
                      className="text-3xl font-bold text-[#2D2D2D] mb-4"
                      style={{ fontFamily: "'DM Serif Display', serif" }}
                    >
                      Demande envoyée !
                    </h2>
                    <p className="text-[#8BC34A] font-semibold text-lg mb-3">
                      Votre petit geste pour la planète a commencé.
                    </p>
                    <p className="text-[#3A3A3A] mb-6">
                      Nous avons bien reçu votre demande de devis. 
                      Notre équipe vous contactera dans les plus brefs délais.
                    </p>
                    <button
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormData({
                          name: "",
                          email: "",
                          phone: "",
                          adresse: "",
                          echeance: "",
                          pose: "",
                          products: [],
                          message: "",
                          acceptTerms: false,
                        });
                      }}
                      className="text-[#8BC34A] hover:underline"
                    >
                      Faire une nouvelle demande
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    onSubmit={handleSubmit}
                    className="bg-white rounded-3xl p-8 md:p-10 shadow-xl space-y-6 border border-[#2D2D2D]/10"
                    data-testid="devis-form"
                  >
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#2D2D2D] mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#2D2D2D]/20 bg-[#F5F5F0] text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#8BC34A]"
                        data-testid="input-name"
                      />
                    </div>

                    {/* Email & Phone */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-[#2D2D2D] mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#2D2D2D]/20 bg-[#F5F5F0] text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#8BC34A]"
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-[#2D2D2D] mb-2">
                          Téléphone *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#2D2D2D]/20 bg-[#F5F5F0] text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#8BC34A]"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    {/* Adresse */}
                    <div>
                      <label htmlFor="adresse" className="block text-sm font-medium text-[#2D2D2D] mb-2">
                        Adresse
                      </label>
                      <input
                        type="text"
                        id="adresse"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-[#2D2D2D]/20 bg-[#F5F5F0] text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#8BC34A]"
                        placeholder="Votre adresse complète"
                        data-testid="input-adresse"
                      />
                    </div>

                    {/* Échéance */}
                    <div>
                      <label className="block text-sm font-medium text-[#2D2D2D] mb-3">
                        Échéance du projet *
                      </label>
                      <div className="flex flex-wrap gap-4">
                        {[
                          { value: "moins3", label: "< 3 mois" },
                          { value: "3a6", label: "de 3 à 6 mois" },
                          { value: "plus6", label: "+ de 6 mois" },
                        ].map((option) => (
                          <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="echeance"
                              value={option.value}
                              checked={formData.echeance === option.value}
                              onChange={handleChange}
                              className="w-4 h-4 text-[#8BC34A] focus:ring-[#8BC34A]"
                            />
                            <span className="text-[#3A3A3A]">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Besoin pose */}
                    <div>
                      <label className="block text-sm font-medium text-[#2D2D2D] mb-3">
                        Avez-vous besoin d'un professionnel pour la pose ?
                      </label>
                      <div className="flex gap-6">
                        {[
                          { value: "oui", label: "Oui" },
                          { value: "non", label: "Non" },
                        ].map((option) => (
                          <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="pose"
                              value={option.value}
                              checked={formData.pose === option.value}
                              onChange={handleChange}
                              className="w-4 h-4 text-[#8BC34A] focus:ring-[#8BC34A]"
                            />
                            <span className="text-[#3A3A3A]">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Products */}
                    <div>
                      <label className="block text-sm font-medium text-[#2D2D2D] mb-3">
                        Quels produits en bambou vous intéressent ? *
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {productOptions.map((product) => (
                          <label key={product.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              name="products"
                              value={product.value}
                              checked={formData.products.includes(product.value)}
                              onChange={handleChange}
                              className="w-4 h-4 rounded text-[#8BC34A] focus:ring-[#8BC34A]"
                            />
                            <span className="text-[#3A3A3A]">{product.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[#2D2D2D] mb-2">
                        Message (optionnel)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl border border-[#2D2D2D]/20 bg-[#F5F5F0] text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#8BC34A] resize-none"
                        placeholder="Décrivez votre projet..."
                        data-testid="input-message"
                      />
                    </div>

                    {/* Terms */}
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="acceptTerms"
                          checked={formData.acceptTerms}
                          onChange={handleChange}
                          className="w-4 h-4 mt-1 rounded text-[#8BC34A] focus:ring-[#8BC34A]"
                        />
                        <span className="text-sm text-[#3A3A3A]">
                          En soumettant ce formulaire, j'accepte que les informations saisies 
                          soient utilisées dans le cadre de la demande de devis et de la relation 
                          commerciale qui peut en découler.
                        </span>
                      </label>
                    </div>

                    {/* Submit */}
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#8BC34A] text-[#2D2D2D] rounded-full px-8 py-4 font-semibold hover:bg-[#A5D66A] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="submit-devis"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Envoi en cours...
                        </span>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Envoyer
                        </>
                      )}
                    </motion.button>

                    <p className="text-xs text-[#9E9E9E] text-center">
                      Les informations recueillies à travers ce formulaire sont destinées à vous envoyer 
                      des réponses à vos demandes et font l'objet d'un traitement par ProBwa.
                    </p>
                  </motion.form>
                )}
              </div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-8"
              >
                <div>
                  <h3
                    className="text-2xl font-bold text-[#2D2D2D] mb-6"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    ou contactez-nous
                  </h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Mail className="w-5 h-5 text-[#8BC34A]" />
                      <h4 className="font-semibold text-[#2D2D2D]">Par e-mail</h4>
                    </div>
                    <a 
                      href={`mailto:${contactInfo.email}`}
                      className="text-[#3A3A3A] hover:text-[#8BC34A] transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>

                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Phone className="w-5 h-5 text-[#8BC34A]" />
                      <h4 className="font-semibold text-[#2D2D2D]">Par téléphone</h4>
                    </div>
                    <a 
                      href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                      className="text-[#3A3A3A] hover:text-[#8BC34A] transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
}
